import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchStoreForecast,
  saveStoreForecast,
  deleteStoreForecast,
} from '@/api/storeForecast'
import type { ReplaceStoreForecastRequest } from '@/api/storeForecast'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'
import { AxiosError } from 'axios'

function extractErrorMessage(error: unknown): string {
  if (error instanceof AxiosError && error.response?.data?.detail) {
    const detail = error.response.data.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail) && detail.length > 0) {
      return detail[0]?.msg ?? 'An error occurred'
    }
  }
  return 'An error occurred'
}

export function useStoreForecast(
  planId: string | undefined,
  versionId: string | undefined,
  lineId: string | undefined
) {
  return useQuery({
    queryKey: queryKeys.storeForecast.byLine(
      planId ?? '',
      versionId ?? '',
      lineId ?? ''
    ),
    queryFn: () => fetchStoreForecast(planId!, versionId!, lineId!),
    enabled: !!planId && !!versionId && !!lineId,
  })
}

export function useSaveStoreForecast(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      lineId,
      body,
    }: {
      lineId: string
      body: ReplaceStoreForecastRequest
    }) => saveStoreForecast(planId, versionId, lineId, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.storeForecast.byLine(
          planId,
          versionId,
          variables.lineId
        ),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
      })
      toast.success('Forecast model saved successfully')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

export function useDeleteStoreForecast(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ lineId }: { lineId: string }) =>
      deleteStoreForecast(planId, versionId, lineId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.storeForecast.byLine(
          planId,
          versionId,
          variables.lineId
        ),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
      })
      toast.success('Forecast model removed successfully')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}
